<template>
    <v-dialog v-model="open" width="640">
        <template #activator="{ on, attrs }">
            <v-btn v-bind="attrs" v-on="on" color="green" class="mx-1" @click="populateOrgs">
                <v-icon class="mr-1">mdi-account-plus</v-icon>
                Add User
            </v-btn>
        </template>
        <v-stepper v-model="step">
            <v-stepper-items>
                <v-stepper-content step="1">
                    <v-card>
                        <v-card-title>Add User</v-card-title>
                        <v-card-subtitle>Select or Add Organization</v-card-subtitle>
                        <v-card-text>
                            <v-form v-model="ok">
                                <v-combobox label="Organization" v-model="org" :search-input.sync="search" :items="orgs" item-text="name" item-value="name" return-object :loading="loading" outlined :rules="orgRules" menu-props="closeOnClick, closeOnContentClick">
                                    <template #no-data>
                                        <v-list-item>
                                            <span class="font-italic">Create {{ search }}</span>
                                        </v-list-item>
                                    </template>
                                </v-combobox>
                                <!-- TODO(branden): should we be able to set email even if this isn't a new org? really we need an org mgmt function. -->
                                <v-text-field v-if="newOrg" label="Organization Email" v-model="orgEmail" :rules="emailRules" hint="The system sends part creation notifications to this address." persistent-hint outlined></v-text-field>
                                <v-text-field v-if="newOrg" label="Pricing Multiplier" v-model="pricingMultiplier" :rules="pricingRules" hint="Price multiplier for this organization (e.g., 1.0 = base, 1.2 = 20% markup)" persistent-hint outlined type="number" step="0.01"></v-text-field>
                            </v-form>
                            <v-banner v-if="alert.open" class="w-100" color="error" single-line rounded>{{ alert.message }}</v-banner>
                        </v-card-text>
                        <v-card-actions>
                            <v-btn color="secondary" @click="close">Cancel</v-btn>
                            <v-spacer />
                            <v-btn color="primary" @click="advance" :loading="loading" :disabled="!ok || loading">Next</v-btn>
                        </v-card-actions>
                    </v-card>
                </v-stepper-content>
                <v-stepper-content step="2">
                    <v-card>
                        <v-card-title>
                            Add User
                            <v-spacer></v-spacer>
                            <v-icon>mdi-account-plus</v-icon>
                        </v-card-title>
                        <v-card-text>
                            <v-form v-if="open" v-model="ok">
                                <v-text-field 
                                    label="Email" 
                                    v-model="email" 
                                    type="email" 
                                    outlined 
                                    required 
                                    :rules="emailRules"
                                ></v-text-field>
                                <v-text-field label="Organization" :value="orgName" outlined disabled></v-text-field>
                                <v-select v-if="canAddEmployee" label="User Type" v-model="type" :items="typeItems" outlined></v-select>
                            </v-form>
                        </v-card-text>
                        <v-card-actions>
                            <v-btn color="secondary" @click="step = 1">Back</v-btn>
                            <v-spacer></v-spacer>
                            <v-btn color="primary" :loading="loading" :disabled="!ok || loading" @click="submit">Add User</v-btn>
                        </v-card-actions>
                    </v-card>
                </v-stepper-content>
            </v-stepper-items>
        </v-stepper>
    </v-dialog>
</template>

<script>
import * as api from '@/api'

export default {
    name: "AddUser",
    props: {
        apiBase: String,
        token: String,
        canAddEmployee: Boolean,
    },
    data: () => ({
        open: false,
        step: 1,
        ok: false,
        loading: false,
        orgs: [],
        users: [],
        search: null,
        alert: {
            open: false,
            message: null,
        },

        orgEmail: null,
        pricingMultiplier: 1.0,
        email: null,
        org: null,
        type: '',

        emailRules: [
            value => !!value || "Email is required",
            value => /[^@\s]+@[^@.\s]+\.[^@.\s]+/.test(value) || "Invalid email",
        ],
        orgRules: [
            value => !!value || "Organization is required",
        ],
        pricingRules: [
            value => value != null && value !== '' || "Pricing multiplier is required",
            value => !isNaN(parseFloat(value)) || "Must be a valid number",
            value => parseFloat(value) > 0 || "Must be greater than 0",
        ],
        typeItems: [
            { text: "Customer", value: "" },
            { text: "Access Employee - Can upload parts and add customer accounts", value: "e" },
            { text: "Access Manager - Can add Access Employee and Manager accounts", value: "m" },
        ],
    }),
    computed: {
        newOrg: function() { 
            if (!this.org) return false
            // If org is a string, it's a new org being created
            if (typeof this.org === 'string') return true
            // If org is an object, check if it's in the list
            return !this.orgs.some(o => o.name === this.org.name)
        },
        orgName: function() {
            if (!this.org) return ''
            return typeof this.org === 'string' ? this.org : this.org.name
        },
        userItems: function() {
            // Map users to autocomplete items with org assignment info
            return this.users.map(user => {
                // Find if this user is assigned to an org
                // We'll need to get this from the backend via a new API or stored locally
                // For now, show "Unassigned"
                const orgAssignment = "Unassigned" // TODO: Get actual org assignment
                return {
                    email: user.email,
                    username: user.username,
                    display: `${user.email} - ${orgAssignment}`,
                    orgAssignment: orgAssignment
                }
            })
        },
    },
    watch: {
        token: function(token) {
            // If the session expires while we have the dialog open, we should
            // see it become null, so we know to close the dialog.
            if (token == null) {
                this.open = false
            }
        },
    },
    methods: {
        close() {
            this.alert.open = this.ok = this.loading = this.open = false
            this.step = 1
            this.orgEmail = this.email = this.org = null
            this.pricingMultiplier = 1.0
            this.type = ''
        },
        async populateOrgs() {
            // NOTE(branden): We don't explicitly synchronize this.loading with
            // create, but populate is called only on mount, so setting loading
            // now prevents create from being called until it's done.
            this.loading = true
            
            // Fetch orgs
            const r = await api.orgsList(this.apiBase, { id: this.token })
            if (r.errorMessage != null) {
                Object.assign(this.alert, { open: true, message: `Couldn't get org list: ${r.errorMessage}` })
                this.loading = false
                return
            }
            this.orgs = r.orgs ?? []
            
            // Fetch users in background
            const usersResult = await api.listUsers(this.apiBase, { id: this.token })
            if (usersResult.errorMessage != null) {
                console.error('Failed to fetch users:', usersResult.errorMessage)
            } else {
                this.users = usersResult.users || []
                console.log('Cognito Users:', this.users)
                console.table(this.users)
            }
            
            this.loading = false
        },
        async createOrg() {
            this.loading = true
            this.alert.open = false
            const orgName = this.orgName // Get the string name
            const r = await api.createOrg(this.apiBase, { id: this.token }, { org: orgName, email: this.orgEmail, pricing_multiplier: this.pricingMultiplier })
            this.loading = false
            if (r.errorMessage != null) {
                Object.assign(this.alert, { open: true, message: `Couldn't create: ${r.errorMessage}` })
                return false
            }
            // Add the new org as an object to the list
            const newOrgObj = {
                name: orgName,
                email: this.orgEmail,
                pricing_multiplier: this.pricingMultiplier,
                uuid: '' // Backend generates this, we don't have it yet
            }
            this.$set(this.orgs, this.orgs.length, newOrgObj)
            // Update org to be the object instead of string
            this.org = newOrgObj
            return true
        },
        async advance() {
            if (this.newOrg) {
                // TODO(branden): we shouldn't actually create the org until submit
                const ok = await this.createOrg()
                if (!ok) {
                    return
                }
                await this.populateOrgs()
                console.log("org data", this.org, this.orgName, this.orgs)
                let orgMade = this.orgs.find(o => o.name == this.org.name)
                console.log("selected org", orgMade)
                this.org = orgMade
            }
            this.step = 2
        },
        async submit() {
            if (!this.ok) {
                // Shouldn't be possible to call this while the form is
                // invalid, but check just in case.
                return;
            }
            this.loading = true;
            
            // Get the org UUID - we need it for assignment
            console.log("org data at time", this.org)
            const orgUUID = typeof this.org === 'object' ? this.org.uuid : null
            if (!orgUUID) {
                this.alert = { open: true, message: 'Organization UUID not found. Please select an existing organization.' }
                this.loading = false
                return
            }
            
            await this.createAndAssignUser(orgUUID)
        },
        async assignExistingUser(orgUUID) {
            // Find the username for the selected email
            const user = this.users.find(u => u.email === this.email)
            if (!user) {
                this.alert = { open: true, message: 'User not found' }
                this.loading = false
                return
            }
            
            const assignParams = {
                username: user.username,
                org_uuid: orgUUID,
            }
            
            console.log('Assigning existing user:', assignParams)
            const assignResult = await api.assignUser(this.apiBase, { id: this.token }, assignParams)
            
            if (assignResult.errorMessage == null) {
                console.log('User assigned successfully')
                this.$emit("success", { email: this.email, org: this.orgName, message: assignResult.message || 'User assigned to organization' })
            } else {
                console.error("Failed to assign user:", assignResult.errorMessage)
                this.$emit("error", assignResult)
            }
           
            this.close()
        },
        async createAndAssignUser(orgUUID) {
            console.log('Selected org:', this.org)
            console.log('Org UUID:', orgUUID)
            
            // Try to create the user first
            const createParams = {
                email: this.email,
                org: this.orgName,
                type: this.type,
            };
            
            console.log('Creating user with params:', createParams)
            const createResult = await api.createUser(this.apiBase, { id: this.token }, createParams);
            console.log('Create user result:', createResult)
            
            let username = null
            let userCreated = false
            
            if (createResult.errorMessage == null) {
                // User created successfully, get their username
                username = createResult.username
                userCreated = true
                console.log("User created successfully with username:", username)
            } else {
                // Check if user already exists
                const errorMsg = String(createResult.errorMessage).toLowerCase()
                console.log("Create user error message:", errorMsg)
                
                if (errorMsg.includes('already exists') || errorMsg.includes('usernameexists')) {
                    // User already exists, use their email as username (Cognito username = email)
                    username = this.email
                    console.log("User already exists, will assign existing user to org")
                } else {
                    // Some other error occurred
                    console.error("Failed to create user:", createResult.errorMessage)
                    this.$emit("error", createResult);
                    this.loading = false
                    return;
                }
            }
            
            // Now assign the user to the org
            if (username) {
                const assignParams = {
                    username: username,
                    org_uuid: orgUUID,
                };
                
                console.log('Assigning user with params:', assignParams)
                const assignResult = await api.assignUser(this.apiBase, { id: this.token }, assignParams);
                console.log('Assign user result:', assignResult)
                
                if (assignResult.errorMessage == null) {
                    const successMessage = userCreated ? 'User created and assigned to organization' : 'Existing user assigned to organization'
                    console.log(successMessage)
                    this.$emit("success", { ...createParams, message: successMessage });
                } else {
                    console.error("Failed to assign user:", assignResult.errorMessage)
                    this.$emit("error", assignResult);
                }
            }
            
            this.$emit('refresh')
            this.close();
        },
    },
}
</script>
